import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import { getToken, removeToken } from '@/utils/auth'
import { tansParams } from '@/utils/params'
import { getApiLang, i18n } from '@/i18n'

export let isRelogin = { show: false }

let onUnauthorized: (() => void) | null = null

export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler
}

function handleUnauthorized() {
  removeToken()
  isRelogin.show = false
  if (onUnauthorized) {
    onUnauthorized()
  } else if (!window.location.pathname.startsWith('/login')) {
    window.location.replace('/login')
  }
}

const baseURL = import.meta.env.VITE_API_BASE || '/api/'

const service = axios.create({
  baseURL,
  timeout: 300000,
  headers: { 'Content-Type': 'application/json;charset=utf-8' },
})

service.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const headers = config.headers || {}
  const isToken = (headers as Record<string, unknown>).isToken === false
  if (getToken() && !isToken) {
    config.headers.Authorization = getToken()!
  }

  const lang = getApiLang()
  if (config.method === 'get' && config.params) {
    let url = `${config.url}?lang=${lang}&${tansParams(config.params as Record<string, unknown>)}`
    url = url.slice(0, -1)
    config.params = {}
    config.url = url
  } else {
    const sep = config.url?.includes('?') ? '&' : '?'
    config.url = `${config.url}${sep}lang=${lang}`
  }
  return config
})

service.interceptors.response.use(
  (res) => {
    const code = res.data?.code ?? 200
    const msg = res.data?.message || res.data?.msg || '请求失败'

    if (res.request?.responseType === 'blob' || res.request?.responseType === 'arraybuffer') {
      return res.data
    }

    if (code === 401 || code === 7401) {
      if (!isRelogin.show) {
        isRelogin.show = true
        handleUnauthorized()
      }
      return Promise.reject(new Error(code === 401 ? i18n.global.t('common.sessionExpired') : msg))
    }

    if (code === 500 || code === 2000) {
      return Promise.reject(new Error(msg))
    }

    if (code !== 200) {
      return Promise.reject(new Error(msg))
    }

    return res.data
  },
  (error) => {
    const data = error?.response?.data
    let message = data?.message || data?.msg || error?.message || '网络异常'
    if (message === 'Network Error') message = i18n.global.t('common.networkError')
    else if (message.includes('timeout')) message = i18n.global.t('common.timeout')
    else if (error?.response?.status === 404) message = data?.message || data?.msg || '接口不存在 (404)'
    return Promise.reject(new Error(message))
  },
)

export default service

export function request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
  return service(config) as Promise<T>
}
